import { Dropdown, MenuItem } from 'react-bootstrap';
import type { ChangeEvent, MouseEvent } from 'react';

import { usePlanSelectorContext } from '../../../contexts/PlanSelectorContext';
import utils from '../../../utils';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import Flex from '../../generic/Flex';
import Icon, { icons } from '../../generic/Icon';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import type { Filter, ServiceFilter, ServiceQueryParameters } from '../../../types';

import useIsGithubApp from './hooks/useIsGithubApp';
import useRefetchGithubAppFiltersOnFocus from './hooks/useRefetchGithubAppFiltersOnFocus';
import ServiceFiltersMenu from './ServiceFiltersMenu';
import { hasGithubAppInstallation } from './utils';
import type { ServiceMetadata } from './types';

export type ServiceImportHeaderProps = {
  serviceDetails: ServiceMetadata;
  filters: ServiceFilter[];
  query: ServiceQueryParameters;
  instanceNames: string[];
  instanceIsConnected: boolean;
  selectedRepoLength: number;
  selectedFilter?: Filter;
  isLoading: boolean;
  /**
   * Overrides whether the import controls (search + import buttons) are shown.
   * When omitted, derived from the legacy `filters` install status. Non-Redux callers
   * (e.g. the vcs-service flow) pass this
   */
  shouldDisplayImportControls?: boolean;
  searchValue: string;
  onSelectFilter: (filterType: string, filterValue: string) => Promise<void>;
  onSelectInstanceName: (instanceName: string) => Promise<void>;
  onSearchChange: (ev: ChangeEvent<HTMLInputElement>) => void;
  onImport: () => Promise<void>;
  onBulkImport: () => void;
  onConnectToService: () => void;
};

const ServiceImportHeader = ({
  serviceDetails,
  instanceNames,
  selectedFilter,
  isLoading,
  selectedRepoLength,
  filters,
  searchValue,
  query,
  instanceIsConnected,
  shouldDisplayImportControls: shouldDisplayImportControlsOverride,
  onSelectInstanceName,
  onSelectFilter,
  onSearchChange,
  onImport,
  onBulkImport,
  onConnectToService,
}: ServiceImportHeaderProps) => {
  const isGithubApp = useIsGithubApp();
  useRefetchGithubAppFiltersOnFocus();
  // Unlike other quick imports, the Github App has an extra step to install the app.
  // Before the user installs the app, we should not display the import controls to avoid confusion.
  // Callers without legacy `filters` (e.g. vcs-service) pass an explicit override.
  const shouldDisplayImportControls =
    shouldDisplayImportControlsOverride ?? (hasGithubAppInstallation({ isGithubApp, filters }) !== 'missing');

  const { showPlanSelectorModal } = usePlanSelectorContext();

  const serviceFilter =
    !instanceIsConnected && !isLoading ? (
      // Github App uses a CTA to connect to the service.
      !isGithubApp && (
        <Button icon={icons.link} type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.MEDIUM} onClick={onConnectToService}>
          Connect with Service
        </Button>
      )
    ) : (
      shouldDisplayImportControls && filters.length > 0 && (
        <ServiceFiltersMenu categories={filters} selectedFilter={selectedFilter} onSelect={onSelectFilter} />
      )
    );

  return (
    <Flex justify="space-between" align="center">
      <Flex gap="8px">
        <div className="btn">
          <Flex align="center">
            <div>
              {/* Let font icon take precedence, but we should slowly adopt SVGIcon to render more complex Logos (e.g., AzureLogo). */}
              {serviceDetails.icon ?
                <Icon size={ICON_SIZE.XL} icon={icons[serviceDetails.icon]} /> :
                serviceDetails.SVGIcon && serviceDetails.SVGIcon({})}
            </div>
            <div>
              &nbsp;{serviceDetails.label}
            </div>
          </Flex>
        </div>
        {instanceNames.length > 1 &&
          <Dropdown
            id="service-instances"
            onSelect={onSelectInstanceName}
            className="service-import-header-btn"
          >
            <Dropdown.Toggle noCaret>
              <Flex justify="space-between">
                <div className="oneline">{query.instanceName}</div>
                <div className="caret" />
              </Flex>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {instanceNames
                .map(name => name && (
                  <MenuItem eventKey={name} active={name === query.instanceName}>
                    {name}
                  </MenuItem>
                ))}
            </Dropdown.Menu>
          </Dropdown>}
        {serviceFilter}
      </Flex>
      {instanceIsConnected && shouldDisplayImportControls &&
        <>
          <div className="input-search-wrapper">
            <input
              type="text"
              className="form-control input-search input-block-level"
              placeholder="Search repos"
              onChange={onSearchChange}
              value={searchValue}
            />
          </div>
          <div className="text-right">
            <Button
              size="medium"
              type="secondary"
              onClick={(ev: MouseEvent) => {
                if (!utils.verify_subscription('premium')) {
                  showPlanSelectorModal();
                  ev.preventDefault();
                } else {
                  onBulkImport();
                }
              }}
            >
              <Icon icon={icons.download} />{' '}Import All
            </Button>
            {' '}
            <Button
              size="medium"
              onClick={onImport}
              isDisabled={!selectedRepoLength || isLoading}
            >
              <b>Import</b>
              {' '}
              {isLoading ? (
                <Icon icon={icons.circleO} hasSpin />
              ) : selectedRepoLength > 0 && (
                <span>&nbsp;{selectedRepoLength}&nbsp;</span>
              )}
              {' '}
              <Icon icon={icons.caretRight} />
            </Button>
          </div>
        </>}
    </Flex>
  );
};

export default ServiceImportHeader;
